// Parse special.name strings for "BUY N GET Y FREE" / "BUY N for $X" / "% off"
// patterns and produce a normalized record used by:
//  - ProductCard promo badge
//  - CartDrawer gamification widget

const RX_BUY_NTH = /buy\s*(\d+)[^\d]+get\s*(\d+)(?:st|nd|rd|th)\s*(?:for\s*\$?(\d+(?:\.\d+)?)|free)/i;
const RX_BUY_GET = /buy\s*(\d+)[^\d]+get\s*(\d+)[^\w]*(free|on us|free!)?/i;
const RX_BUY_FOR = /buy\s*(\d+)[^\d]+for\s*\$?(\d+(?:\.\d+)?)/i;
const RX_PCT = /(\d+)\s*%\s*off/i;

export function classifySpecial(special) {
    const raw = special?.name || "";
    if (!raw) return null;

    // "Buy 1 Get 2nd for $1" / "Buy 2 Get 3rd Free" — ordinal pattern.
    // Total units required = the ordinal (Nth item triggers the deal).
    let m = raw.match(RX_BUY_NTH);
    if (m) {
        const buyN = parseInt(m[1], 10);
        const ordN = parseInt(m[2], 10);
        const dollars = m[3] ? parseFloat(m[3]) : null;
        return {
            id: special.id,
            kind: "BOGO",
            buyN,
            getY: 1,
            threshold: ordN, // 2 for "Get 2nd", 3 for "Get 3rd", etc.
            name: raw,
            short:
                dollars != null
                    ? `BUY ${buyN}, ${ordN}TH FOR $${dollars}`
                    : `BUY ${buyN}, GET ${ordN}TH FREE`,
        };
    }

    m = raw.match(RX_BUY_GET);
    if (m) {
        const buyN = parseInt(m[1], 10);
        const getY = parseInt(m[2], 10);
        return {
            id: special.id,
            kind: "BOGO",
            buyN,
            getY,
            // total items required in cart to unlock
            threshold: buyN + getY,
            name: raw,
            short: `BUY ${buyN}, GET ${getY} FREE`,
        };
    }
    m = raw.match(RX_BUY_FOR);
    if (m) {
        const buyN = parseInt(m[1], 10);
        const dollars = parseFloat(m[2]);
        return {
            id: special.id,
            kind: "BUY_FOR",
            buyN,
            dollars,
            threshold: buyN,
            name: raw,
            short: `${buyN} FOR $${dollars}`,
        };
    }
    m = raw.match(RX_PCT);
    if (m) {
        return {
            id: special.id,
            kind: "PCT_OFF",
            pct: parseInt(m[1], 10),
            name: raw,
            short: `${m[1]}% OFF`,
        };
    }
    return {
        id: special.id,
        kind: "OTHER",
        name: raw,
        short: raw.length > 28 ? raw.slice(0, 26) + "…" : raw,
    };
}

// Returns a compact discount sticker for a product card image.
// Priority order:
//   1. Variant-level sale (specialPriceRec < priceRec): prefer "% OFF" when
//      the implied percentage is >=5%, otherwise show absolute "$ OFF".
//   2. Dutchie special tied to the variant:
//        - PCT_OFF special   -> "{pct}% OFF"
//        - BOGO / BUY_FOR    -> no sticker (the named badge below the image
//                                already describes the structural deal).
// Returns null when no quantifiable discount applies.
export function discountSticker(variant, special) {
    if (!variant) return null;
    const reg = Number(variant.priceRec) || 0;
    const sale = Number(variant.specialPriceRec) || 0;
    if (sale > 0 && reg > 0 && sale < reg) {
        const diff = reg - sale;
        const pct = Math.round((diff / reg) * 100);
        if (pct >= 5) {
            return { kind: "PCT", label: `${pct}% OFF`, value: pct };
        }
        return { kind: "DOLLAR", label: `$${diff.toFixed(2)} OFF`, value: diff };
    }
    if (special) {
        const c = classifySpecial(special);
        if (c?.kind === "PCT_OFF" && c.pct > 0) {
            return { kind: "PCT", label: `${c.pct}% OFF`, value: c.pct };
        }
    }
    return null;
}

// Returns the verbatim Dutchie special.name(s) of deals that have actually
// fired on the current cart. Dutchie Plus's GraphQL schema does NOT expose the
// applied-deal name directly on Checkout, so we resolve it by:
//   1. taking every item where item.isDiscounted === true,
//   2. collecting that item's variant.specialConditionIds + specialRewardIds,
//   3. intersecting with the specials list returned by Dutchie.
// Returns an array of unique special.name strings (may be empty).
export function appliedDealNames(specials, cartItems) {
    if (!specials?.length || !cartItems?.length) return [];
    const appliedIds = new Set();
    for (const it of cartItems) {
        if (!it.isDiscounted) continue;
        const variant =
            (it.product?.variants || []).find((v) => v.option === it.option) ||
            (it.product?.variants || [])[0];
        if (!variant) continue;
        for (const sid of variant.specialConditionIds || []) appliedIds.add(sid);
        for (const sid of variant.specialRewardIds || []) appliedIds.add(sid);
    }
    if (appliedIds.size === 0) return [];
    const names = [];
    const seen = new Set();
    for (const s of specials) {
        if (!appliedIds.has(s.id)) continue;
        const n = s.name || "";
        if (!n || seen.has(n)) continue;
        seen.add(n);
        names.push(n);
    }
    return names;
}

// Same lookup as appliedDealNames but returns the special.id Set. CartDrawer
// uses this to suppress the in-progress card for a specific deal that's
// already fired, while STILL showing other deals the cart hasn't completed.
export function appliedDealIds(specials, cartItems) {
    if (!specials?.length || !cartItems?.length) return new Set();
    const appliedIds = new Set();
    for (const it of cartItems) {
        if (!it.isDiscounted) continue;
        const variant =
            (it.product?.variants || []).find((v) => v.option === it.option) ||
            (it.product?.variants || [])[0];
        if (!variant) continue;
        for (const sid of variant.specialConditionIds || []) appliedIds.add(sid);
        for (const sid of variant.specialRewardIds || []) appliedIds.add(sid);
    }
    // Filter to ids actually present in the store's specials list (so we
    // don't accidentally suppress legitimate progress cards via stale ids).
    const known = new Set(specials.map((s) => s.id));
    const out = new Set();
    for (const id of appliedIds) if (known.has(id)) out.add(id);
    return out;
}

// Cart-eligibility-aware progress. Returns an ARRAY of every BOGO/BUY_FOR
// deal the current cart is eligible for (has at least one matching unit) but
// hasn't yet completed. Sorted by smallest unmet threshold first so the
// "easiest win" gamification card surfaces at the top.
export function activeBogoProgresses(specials, cartItems) {
    if (!specials?.length || !cartItems?.length) return [];

    const unitsPerSpecial = {};
    for (const it of cartItems) {
        const qty = it.quantity || 0;
        if (qty < 1) continue;
        const variant =
            (it.product?.variants || []).find((v) => v.option === it.option) ||
            (it.product?.variants || [])[0];
        const ids = [
            ...((variant && variant.specialConditionIds) || []),
            ...((variant && variant.specialRewardIds) || []),
        ];
        for (const sid of ids) {
            unitsPerSpecial[sid] = (unitsPerSpecial[sid] || 0) + qty;
        }
    }
    if (Object.keys(unitsPerSpecial).length === 0) return [];

    const out = [];
    for (const s of specials) {
        const c = classifySpecial(s);
        if (!c) continue;
        if (c.kind !== "BOGO" && c.kind !== "BUY_FOR") continue;
        const eligibleUnits = unitsPerSpecial[c.id] || 0;
        if (eligibleUnits < 1) continue;
        const needed = Math.max(0, c.threshold - eligibleUnits);
        if (needed > 0 && needed <= 6) {
            out.push({ ...c, needed, current: eligibleUnits });
        }
    }
    out.sort((a, b) => a.needed - b.needed);
    return out;
}

// Back-compat: returns just the top entry. Existing callers stay working.
export function activeBogoProgress(specials, cartItems) {
    const all = activeBogoProgresses(specials, cartItems);
    return all[0] || null;
}
